import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import camiyuiLogo from '../../assets/camiyui-logo.jpg';
import InstallPrompt from '../../components/InstallPrompt';
import LoginNotificationPrompt from '../../components/LoginNotificationPrompt';
import { STORE_DISPLAY_NAME } from '../../constants/branding';
import { resolveCustomerInStoreSetupCode } from '../../utils/login/storeSetup';

const normalizeCodeInput = (value: string): string =>
  value.replace(/\D+/g, '').slice(0, 6);

const StoreSetupPage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmittedCodeRef = useRef('');

  const handleContinue = useCallback(async (codeOverride?: string) => {
    const normalizedCode = normalizeCodeInput(codeOverride ?? code);
    if (normalizedCode.length !== 6) {
      toast.warning('Please enter the 6-digit store code from our staff.');
      return;
    }
    if (isSubmitting || lastSubmittedCodeRef.current === normalizedCode) {
      return;
    }

    lastSubmittedCodeRef.current = normalizedCode;
    setIsSubmitting(true);
    try {
      const result = await resolveCustomerInStoreSetupCode(normalizedCode);
      if (!result.ok) {
        if (result.status === 404 || result.status === 409 || result.status === 410) {
          toast.info(result.message);
          return;
        }
          toast.error(result.message);
          return;
        }

      window.location.assign(result.data.redirect_url);
    } finally {
      setIsSubmitting(false);
    }
  }, [code, isSubmitting]);

  useEffect(() => {
    if (code.length === 6) {
      void handleContinue(code);
    } else {
      lastSubmittedCodeRef.current = '';
    }
  }, [code, handleContinue]);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#e8f5f1_0%,#f8fafc_42%,#eef5f8_100%)] px-4 py-8">
      <LoginNotificationPrompt />

      <main className="mx-auto max-w-md">
        <section className="mb-5 px-2 pt-3 pb-1">
          <div className="flex items-center justify-center gap-3">
            <div className="h-14 w-14 overflow-hidden rounded-[18px] border border-white/70 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
              <img
                src={camiyuiLogo}
                alt={STORE_DISPLAY_NAME}
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                {STORE_DISPLAY_NAME}
              </p>
              <h1 className="mt-1 text-[28px] font-semibold tracking-[-0.03em] text-slate-950">
                In-Store Help
              </h1>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[32px] border border-emerald-100 bg-white/90 shadow-lg shadow-emerald-100/60 backdrop-blur">
          <div className="border-b border-emerald-100/80 bg-white px-5 py-5">
            <h2 className="text-xl font-semibold tracking-[-0.02em] text-slate-950">
              Enter your 6-digit store code
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Ask our staff for your 6-digit code, then continue to setup or password reset.
            </p>
          </div>

          <div className="space-y-5 p-5">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <KeyRound className="h-4 w-4 text-emerald-700" />
                6-digit store code
              </div>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(normalizeCodeInput(e.target.value))}
                placeholder="6-digit code"
                className="w-full rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4 text-center text-2xl font-semibold tracking-[0.35em] text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
              <p className="text-xs leading-5 text-slate-500">
                We will continue automatically after all 6 digits are entered.
              </p>
            </div>

            <button
              type="button"
              onClick={() => void handleContinue()}
              disabled={isSubmitting}
              className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[22px] bg-slate-950 px-4 py-4 text-base font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Continue
              <ChevronRight className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full rounded-[22px] border border-slate-200 bg-white px-4 py-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Back to login
            </button>
          </div>

          <div className="px-5 pb-5">
            <InstallPrompt mode="embedded" />
          </div>
        </section>
      </main>
    </div>
  );
};

export default StoreSetupPage;
